import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import { useEffect, useCallback } from "react";

type UserRole = {
  role: string | null;
};

export type User = {
  id: string;
  full_name: string;
  email?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user_roles: UserRole | null;
};

const usersAtom = atom<User[]>([]);

export function useUsers() {
  const [users, setUsers] = useAtom(usersAtom);

  const fetchUsers = useCallback(async () => {
    try {
      const { error, data } = await supabase
        .from("users")
        .select(
          `
          *,
          user_roles(role)
        `
        )
        .is("deleted_at", null)
        .order("full_name", { ascending: true });

      if (error) throw error;
      setUsers(data ?? []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, [setUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return [users, fetchUsers] as const;
}

export async function fetchUsersForManager() {
  const { data, error } = await supabase
    .from("users")
    .select(
      `
      *,
      user_roles(role)
    `
    )
    .is("deleted_at", null)
    .order("full_name", { ascending: true });

  if (error) throw error;

  return {
    data: data ?? [],
    hasMore: false,
  };
}

export async function searchUsers(query: string, limit = 10) {
  const { data, error } = await supabase
    .from("users")
    .select("*")
    .is("deleted_at", null)
    .or(`full_name.ilike.%${query}%,email.ilike.%${query}%`)
    .order("full_name", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

export async function fetchUserById(id: string) {
  const { data, error } = await supabase
    .from("users")
    .select(
      `
      *,
      user_roles(role)
    `
    )
    .eq("id", id)
    .is("deleted_at", null)
    .single();

  if (error) throw error;
  return data;
}
